import { Component, DestroyRef, inject, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { Router } from '@angular/router'

import { Enums, Profile } from '@cyborg-monorepo/hunter/common'
import { DialogRef, DialogService } from '@hyperkit'
import { ThemeService } from '@hyperkit'

import { VerityEnvironmentService } from '../../../../../environments/verity-environment.service'
import { AuthService } from '../../../../auth/auth.service'
import { ReleaseService } from '../../../../changelog/release.service'
import { FeatureFlagService } from '../../../../helpers/feature-flag.service'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service'
import { SearchStateService } from '../../../research/services/search-state.service'
import { SearchUrlService } from '../../../research/services/search-url.service'
import { CalendlyService } from '../../services/calendly.service'
import { FrameDetectionService } from '../../services/frame-detection.service'
import { UserService } from '../../services/user.service'
import { HuntModuleDialogComponent } from '../dialogs/hunt-module-dialog/hunt-module-dialog.component'
import { TalkToCyborgDialogComponent } from '../dialogs/talk-to-cyborg-dialog/talk-to-cyborg-dialog.component'

import { shouldShowTrialBanner } from './trial-banner.helpers'
import { shouldShowVerityBanner } from './verity-move-banner.helpers'

const getURLPath = (): string | null => {
  const pathname = window.location.pathname // Get the path (e.g., "/hunt/dashboard")
  const firstPath = pathname.split('/').filter((segment) => segment)[0] // Extract the first segment
  return firstPath || null
}

@Component({
  standalone: false,
  selector: 'header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit {
  private searchStateService = inject(SearchStateService)
  private searchUrlService = inject(SearchUrlService)
  private userService = inject(UserService)
  private authService = inject(AuthService)
  private releaseService = inject(ReleaseService)
  private dialogService = inject(DialogService)
  private featureFlagService = inject(FeatureFlagService)
  private calendlyService = inject(CalendlyService)
  private trackingService = inject(TrackingService)
  private frameDetectionService = inject(FrameDetectionService)
  private themeService = inject(ThemeService)
  private verityEnvironmentService = inject(VerityEnvironmentService)

  private readonly destroyRef = inject(DestroyRef) // inject destruction context

  private router = inject(Router)
  public firstName: string
  public lastName: string
  public role: string
  public searchForm: FormGroup
  public theme = this.themeService.theme
  public loggedIn: boolean
  public latest: string
  public daysRemaining = 30
  public daysThreshold = 30
  public status: Enums.CompanyStatus
  public expires: Date
  public statusTypes = Enums.CompanyStatus
  public huntModuleAccess = false
  public activeRoute = ''
  public isiFrame = false
  public showVerityBanner = false
  public showTrialBanner = false

  constructor() {
    this.router.events.subscribe(() => {
      const fullPath = this.router.url // Get the current route
      this.activeRoute = '/' + fullPath.split('/')[1] // Extract the first segment (e.g., "/hunt")
    })
  }

  public isActive(link: string): boolean {
    return this.activeRoute === link
  }

  public ngOnInit(): void {
    if (this.frameDetectionService.isIframe()) {
      this.isiFrame = true
    }

    this.searchForm = new FormGroup({
      search: new FormControl('', [Validators.required]),
    })

    this.latest = this.releaseService.getLatestVersion()
    this.huntModuleAccess = this.featureFlagService.huntModuleAccess()

    if (this.authService.isAuthenticated()) {
      this.loggedIn = true
      // listen for profile changes, ex. if user reactivates expired profile
      this.userService.profile$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((profile: Profile | null) => {
          if (!profile) return

          this.firstName = profile.firstName
          this.lastName = profile.lastName
          this.role = profile.role
          this.status = profile.company.status
          this.expires = new Date(profile.company.expires)
          this.showVerityBanner = shouldShowVerityBanner(
            this.isiFrame,
            profile.company.verityCustomerCode,
            this.status,
            profile.verityRedirectCutoff,
          )
          this.showTrialBanner = shouldShowTrialBanner(
            this.isiFrame,
            profile.company.verityCustomerCode,
            this.status,
          )

          const oneDay = 24 * 60 * 60 * 1000
          const now = new Date()

          // calculate days remaining of trial period
          this.daysRemaining = Math.max(
            0,
            Math.ceil((this.expires.valueOf() - now.valueOf()) / oneDay),
          )
        })
    } else {
      this.loggedIn = false
    }
  }

  public onLogOut(): void {
    this.userService.clearProfile()
    this.authService.logout()
  }

  public openPremiumDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_TALK_WITH_US_DIALOG_OPENED,
    )
    const dialogRef: DialogRef = this.dialogService.open(
      TalkToCyborgDialogComponent,
    )

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg()
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_TALK_WITH_US_DIALOG_ABANDONED,
        )
      }
    })
  }

  public openHuntDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_OPENED,
    )
    const dialogRef: DialogRef = this.dialogService.open(
      HuntModuleDialogComponent,
      {
        panelClass: 'full-panel',
      },
    )

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg()
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_ABANDONED,
        )
      }
    })
  }

  public talkToCyborg(): void {
    this.calendlyService.talkToCyborg()
  }

  public onVerityBannerAction(): void {
    window.open(
      this.verityEnvironmentService.verityConfig.appUrl,
      '_blank',
      'noopener,noreferrer',
    )
  }

  public onVerityBannerContactSupport(): void {
    const subject = encodeURIComponent('Get Access to Hunt Mode in Verity')
    const body = encodeURIComponent(
      "I'd like to get access to Hunt Mode in the Verity platform.",
    )
    window.location.href = `mailto:support@intel471.com?subject=${subject}&body=${body}`
  }

  public onSearch(): void {
    const term: string = this.searchForm.value.search
    if (term) {
      this.searchStateService.setTerm(term)
      this.searchUrlService.gotoUrl(this.searchStateService.state.value)
      this.searchForm.setValue({ search: '' })
    }
  }
}
